// umlaut-check: skip — technische Schlüssel der Bestandskennzahlen (JSON-Schnittstelle).
import {qtyNum} from './format';

// Bestands- und Verfügbarkeitslogik des Originals (Zeilen 843–947, 1046,
// 1368–1397, 4650). Die Prüfreihenfolge in `availCat` ist Fachlogik und wird
// exakt so übernommen — jede Umstellung ändert die Ampeln im Betrieb.

/** Lager-IDs der drei Stammlager (Original: WH_OF_STAMM). */
export const WH_OF_STAMM = {Lahr: '3827', SNV: '8034', Cosmos: '2350209'};

/**
 * Dritte Wahl am Schieberegler: „Keine Lieferung" (PLAN.md 3.10, ABGLEICH
 * H27–H36). Sie ist bewusst KEIN Lager — sie hat keine Lager-ID, kein
 * Bestandswert und keine Farbe. Der Wert wird im Ordermanagement je Position
 * gehalten (`order_editor_states.item_overrides`), nicht im weclapp-Feld
 * „Stammlager": dort stünde eine Angabe, die fachlich kein Lager ist.
 */
export const STAMM_NONE = 'none';

/** Beschriftungen der Segmente (Original `label` in `lagerSlider`). */
export const STAMM_LABELS = {Lahr: 'Lahr', SNV: 'Schneider', Cosmos: 'Cosmos', [STAMM_NONE]: 'Keine'};

/** Anzeigename einer Lagerwahl. */
export function stammLabel(value) {
    return STAMM_LABELS[value] || value || '';
}

/**
 * Benennung in der Änderungsliste (Original `computeChanges`, Zeile 5145):
 * dort steht der rohe Wert, nicht die Segment-Beschriftung.
 */
export function stammDiffLabel(value) {
    if (!value) {
        return '(Standard)';
    }
    return value === STAMM_NONE ? 'Keine Lieferung' : value;
}

/** Ist diese Position von der Lieferung ausgenommen („Keine")? */
export function isExcluded(r) {
    return !!(r && r._stammOverride === STAMM_NONE);
}

/** Farbdosen (Burner/Premium mit VOC-Wert) kommen zwingend von Schneider. */
export function isSchneiderArticle(r, stock) {
    const name = (stock.names && stock.names[String(r.articleId)]) || r.title || '';
    return /\b(burner|premium)\b/i.test(name) && ((stock.voc && stock.voc[String(r.articleId)]) || 0) > 0;
}

/**
 * Stammlager einer Position: 1) Positions-Zuordnung aus dem Beleg,
 * 2) Artikel-Stammlager aus weclapp, 3) Sortiments-Logik.
 */
export function stammLagerOf(r, stock) {
    if (r._stammOverride) {
        return r._stammOverride;
    }
    const v = stock.stammArt && stock.stammArt[String(r.articleId)];
    if (v) {
        return v;
    }
    return isSchneiderArticle(r, stock) ? 'SNV' : 'Lahr';
}

/**
 * Lager, aus dem diese Position bedient wird — `null` bei „Keine Lieferung"
 * (ABGLEICH H28). Der Sentinel muss von jedem Aufrufer behandelt werden, sonst
 * greift der Rückfall auf Lahr und die Position bekäme wieder einen Bestand.
 */
export function sourceWarehouseFor(r, stock, order) {
    if (isExcluded(r)) {
        return null;
    }
    return WH_OF_STAMM[stammLagerOf(r, stock)] || (order && order.warehouseId) || '3827';
}

/**
 * Kennzahlen der einheitlichen Bestandsregel (Auftrag #315) für das Lieferlager
 * der Position: `lagerbestand`, `reserviert_fremd`, `reserviert_eigen`,
 * `verfuegbar` und die graue Info `offen_nicht_reserviert`. `undefined`, solange
 * der Bestand fehlt oder die Position nicht geliefert wird.
 */
export function availOf(r, stock, order) {
    if (!stock.loaded) {
        return undefined;
    }
    const w = sourceWarehouseFor(r, stock, order);
    if (w === null) {
        return undefined;
    }
    const byWh = (stock.avail || {})[String(r.articleId)];
    return byWh ? byWh[w] : undefined;
}

/**
 * Verfügbarer Bestand im zuständigen Lager = Lagerbestand − von ANDEREN
 * Aufträgen hart reserviert + von DIESEM Auftrag schon reserviert. Offene,
 * nicht reservierte Mengen anderer Aufträge zählen nicht mit. Rückfall auf den
 * Wert am Auftragslager.
 */
export function freeOf(r, stock, order) {
    if (!stock.loaded) {
        return undefined;
    }
    const a = String(r.articleId);
    const w = sourceWarehouseFor(r, stock, order);
    if (w === null) {
        return undefined;
    }
    const byWh = stock.freeByWh[a];
    if (byWh && byWh[w] !== undefined) {
        return byWh[w];
    }
    return stock.free[a];
}

/** Lagerbestand (physisch) im zuständigen Lager. */
export function physOf(r, stock, order) {
    if (!stock.loaded) {
        return undefined;
    }
    const w = sourceWarehouseFor(r, stock, order);
    if (w === null) {
        return undefined;
    }
    const byWh = stock.whStock[String(r.articleId)];
    return byWh && byWh[w] !== undefined ? byWh[w] : undefined;
}

/** Für diesen Auftrag per Entnahme gesicherte Menge. */
export function pickedOf(r) {
    return parseFloat(r.pickedQty || 0) || 0;
}

/** Verfügbarer Bestand des Artikels in einem bestimmten Lager (Schieberegler). */
export function freeAtWh(r, wh, stock) {
    const b = stock.freeByWh[String(r.articleId)];
    return b && b[wh] !== undefined ? b[wh] : undefined;
}

/**
 * Verfügbarkeits-Kategorie: -1 unbekannt, 0 nicht verfügbar, 1 teilweise,
 * 2 verfügbar, 3 verfügbar durch Produktion. Reihenfolge exakt wie im Original.
 */
export function availCat(r, stock, order) {
    if (!stock.loaded || isExcluded(r)) {
        return -1;
    }
    const needD = qtyNum(r.quantity) - qtyNum(r.shippedQuantity);
    // Durch Entnahme gesichert gilt als verfügbar, egal was „verfügbar" meldet.
    if (needD > 0 && pickedOf(r) >= needD) {
        return 2;
    }
    const v = freeOf(r, stock, order);
    if (v === undefined) {
        return -1;
    }
    const need = parseFloat(String(r.quantity).replace(',', '.')) || 0;
    if (v >= need) {
        return 2;
    }
    if (stock.production[String(r.articleId)]) {
        return 3;
    }
    return v <= 0 ? 0 : 1;
}

/** Lieferbare Menge = min(Menge, verfügbar), nie negativ. */
export function deliverableQty(r, stock, order) {
    if (isExcluded(r)) {
        return 0;
    }
    const q = qtyNum(r.quantity);
    if (!stock.loaded) {
        return q;
    }
    const free = freeOf(r, stock, order);
    if (free === undefined) {
        return q;
    }
    return Math.max(0, Math.min(q, free));
}

// ── VE-Regel (Auftrag #315, Mail Sebastian Fischinger 28.09.2026, Punkt 5) ─────
// Alle Vertriebswege außer PG1 bestellen ganze Verkaufseinheiten (VE). Ist
// weniger verfügbar als offen, wird auf die nächste ganze VE ABGERUNDET; die
// Differenz geht in die FM-Datei. Dieselbe Rechnung steht im Server
// (VeRegel::liefermenge); der Server korrigiert den Lieferschein nach dem
// Anlegen verbindlich, die Oberfläche zeigt nur vorab, was dabei herauskommt.
// Annahmen V1–V3 und die ausgenommenen Vertriebswege kommen vom Server
// (`stock.veRegel`, config/order_editor.php → ve_regel).

/** VE-Größe des Artikels dieser Position; `null` ohne Wert oder bei VE ≤ 1 (V1). */
export function veOf(r, stock) {
    const v = Number((stock.ve || {})[String(r.articleId)]);
    return v > 1 ? v : null;
}

/** Gilt die VE-Regel für den Vertriebsweg des Auftrags (V3)? Unbekannter Weg: nein. */
export function veGilt(stock, order, pending) {
    const regel = stock.veRegel;
    if (!regel || !regel.aktiv) {
        return false;
    }
    const code = String((pending && pending.channel) || (order && order.salesChannel) || '').trim().toUpperCase();
    if (!code) {
        return false;
    }
    return !(regel.ausgenommen || []).map((x) => String(x).trim().toUpperCase()).includes(code);
}

/** Liefermenge nach der VE-Regel (V1/V2), spiegelt `VeRegel::liefermenge`. */
export function veLiefermenge(offen, moeglich, ve) {
    const m = Math.max(0, moeglich);
    if (!ve || ve <= 1.0001) {
        return m;
    }
    if (m >= offen - 0.0001) {
        return m;
    }
    return Math.round(Math.floor(Math.round((m / ve) * 1e6) / 1e6) * ve * 1000) / 1000;
}

/**
 * Positionen, bei denen die VE-Regel die Liefermenge unter die lieferbare Menge
 * senkt — Vorschau vor dem Anlegen der Lieferscheine. `liefern` kommt auf den
 * Lieferschein, `fm` (offen − liefern) in die FM-Datei.
 */
export function veVorschau(rows, stock, order, pending) {
    if (!stock.loaded || !veGilt(stock, order, pending)) {
        return [];
    }
    const kitParents = new Set((rows || []).filter((r) => r.parentItemId).map((r) => String(r.parentItemId)));
    const out = [];

    for (const r of rows || []) {
        if (r.del || r._new || !r.articleId || isExcluded(r) || (r.id != null && kitParents.has(String(r.id)))) {
            continue;
        }
        const ve = veOf(r, stock);
        const free = freeOf(r, stock, order);
        if (!ve || free === undefined) {
            continue;
        }
        const offen = qtyNum(r.quantity) - qtyNum(r.shippedQuantity);
        if (offen <= 0) {
            continue;
        }
        const moeglich = Math.min(offen, Math.max(0, free));
        const liefern = veLiefermenge(offen, moeglich, ve);
        if (liefern >= moeglich - 0.0001) {
            continue;
        }
        out.push({
            key: String(r.id || r._key || r.articleId),
            articleId: r.articleId,
            articleNumber: r.articleNumber || '',
            title: r.title || '',
            ve,
            offen,
            verfuegbar: moeglich,
            liefern,
            fm: Math.round((offen - liefern) * 1000) / 1000,
            row: r,
        });
    }
    return out;
}

/**
 * Stammlager einer Position OHNE Positions-Zuordnung (Original `articleStammOf`,
 * Zeile 1818) — Grundlage für „weicht vom Stammlager ab" und den Rücksetzer.
 */
export function articleStammOf(r, stock) {
    const v = stock.stammArt && stock.stammArt[String(r.articleId)];
    if (v) {
        return v;
    }
    return isSchneiderArticle(r, stock) ? 'SNV' : 'Lahr';
}

/**
 * Weicht mindestens eine Position von ihrem Artikel-Stammlager ab (Original
 * `hasStammDeviation`, Zeile 1824)? Steuert den Master-Schalter „↺ Stammlager".
 */
export function hasStammDeviation(rows, stock) {
    return (rows || []).some(
        (r) => !r.del && r.articleId && r._stammOverride && r._stammOverride !== articleStammOf(r, stock),
    );
}

/** Positionen, die per Schieberegler vom Artikel-Stammlager abweichen. */
export function deviatingRows(rows, stock) {
    return (rows || []).filter(
        (r) => !r.del && r.articleId && r._stammOverride && r._stammOverride !== articleStammOf(r, stock),
    );
}

/** Rabattgruppen-Schlüssel einer Position. */
export function groupOf(r, stock) {
    return (stock.groups && stock.groups[String(r.articleId)]) || 'none';
}

/** Anzeigename der Rabattgruppe. */
export function groupLabelOf(r, stock) {
    const k = groupOf(r, stock);
    if (k === 'none') {
        return '';
    }
    const g = (stock.rules || []).find((x) => x.key === k);
    return g ? g.label : k;
}

/**
 * Trägt das Lager dieser Position die Kennzeichnung „in Pflege"? Solange der
 * Lahr-Bestand in weclapp aufgebaut wird, sind die dortigen Zahlen Platzhalter
 * (Einstellung `ORDER_EDITOR_LAHR_STOCK_TRUSTED`).
 */
export function isStockInPflege(r, stock, order) {
    if (stock.lahrStockTrusted !== false) {
        return false;
    }
    return sourceWarehouseFor(r, stock, order) === WH_OF_STAMM.Lahr;
}

/** Trägt ein bestimmtes Lager die Kennzeichnung „in Pflege"? */
export function isWarehouseInPflege(warehouseId, stock) {
    if (stock.lahrStockTrusted !== false) {
        return false;
    }
    const wh = (stock.warehouses || []).find((w) => String(w.id) === String(warehouseId));
    if (wh && wh.trusted !== undefined) {
        return wh.trusted === false;
    }
    return String(warehouseId) === WH_OF_STAMM.Lahr;
}
